import { type CancelToken } from 'axios';
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createCancelToken } from '~/_shared/utils/api/api.helpers';
import {
  isApiError, isCancelError,
} from '~/_shared/utils/api/apiError.helpers';
import { debounce } from '~/_shared/utils/debounce';
import { useTranslation } from '~/_shared/utils/hooks';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { isNullsy } from '~/_shared/utils/typeGuards';
import type { ConstraintsValidationDataMap } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.types';
import type { BoundaryId } from '~/store/boundaries/boundaryIdentifier.type';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import type { FilterTreeItemRequest } from '~/store/spreadsheetData/filtering/spreadsheetDataFiltering.helpers';
import {
  type ValidateMOTConstraintsItem,
  validateOptimizedTerritoryConstraints,
} from '../../../createOptimizedTerritoriesModal/optimizedTerritories.repository';
import { type MetricValue } from '../../createBoundaryGroupFromAIModal.types';
import { getConstraintValidationErrorMessages } from './constraints.helpers';

const executeDebounced = debounce((fn: () => void) => fn(), 750);

type OptimizedTerritoriesConstraintsValidationParams = {
  constraintsEnabled: boolean;
  dataMetricsValues: readonly MetricValue[];
  demographicsValues: readonly MetricValue[];
  boundaryGroupId: number | null;
  blockSubset?: readonly BoundaryId[];
  filter?: Maybe<FilterTreeItemRequest>;
  excludeDisconnectedBlocks: boolean;
};

export type MOTConstraintValidationState = {
  areConstraintsValid: boolean;
  generalError: string;
  isLoading: boolean;
  validationData: ConstraintsValidationDataMap;
};

export const useOptimizedTerritoriesConstraintsValidation = ({
  boundaryGroupId,
  constraintsEnabled,
  demographicsValues,
  dataMetricsValues,
  blockSubset,
  filter,
  excludeDisconnectedBlocks,
}: OptimizedTerritoriesConstraintsValidationParams): MOTConstraintValidationState => {
  const [validationData, setValidationData] = useState<ConstraintsValidationDataMap>(new Map());
  const [generalError, setGeneralError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [t] = useTranslation();
  const clientId = useClientIdSelector();

  const spreadsheetId = usePrimarySpreadsheetId();
  const mapId = useMapIdSelector();

  const constraints = useMemo(() => [
    ...dataMetricsValues.filter(v => v.checked),
    ...demographicsValues.filter(v => v.checked),
  ], [dataMetricsValues, demographicsValues]);

  const validateConstraints = useCallback(async (cancelToken: CancelToken) => {
    if (isNullsy(clientId) || isNullsy(mapId) || isNullsy(spreadsheetId) || isNullsy(boundaryGroupId)) {
      return;
    }

    const validationItems: ValidateMOTConstraintsItem[] = constraints.map(constraint => ({
      attribute: constraint.id,
      lower_bound: constraint.constraintLowerBound ?? 0,
      upper_bound: constraint.constraintUpperBound ?? Number.MAX_SAFE_INTEGER,
    }));

    if (validationItems.length === 0) {
      return;
    }

    setIsLoading(true);

    try {
      const validationResult = await validateOptimizedTerritoryConstraints(clientId, {
        map_id: mapId,
        constraints: validationItems,
        boundary_group_id: boundaryGroupId,
        spreadsheet_id: spreadsheetId,
        block_subset: blockSubset,
        filter: filter ? { filter_tree: filter.filterTree } : undefined,
        exclude_disconnected_blocks: excludeDisconnectedBlocks,
      }, cancelToken);

      setGeneralError('');

      if (validationResult && validationResult.results.length) {
        const newValidationData: ConstraintsValidationDataMap = new Map();

        for (const validationItem of validationResult.results) {
          const errorMessages = getConstraintValidationErrorMessages(validationItem, t);

          newValidationData.set(validationItem.attribute, {
            errorMessages,
            highestMin: validationItem.limits.highestMin,
            lowestMax: validationItem.limits.lowestMax,
            max: validationItem.limits.max,
            min: validationItem.limits.min,
          });
        }

        setValidationData(newValidationData);
      }
    }
    catch (err) {
      if (!isApiError(err) || !isCancelError(err)) {
        setValidationData(new Map());
        setGeneralError(t('Something went wrong, please try again.'));
      }
    }
    finally {
      setIsLoading(false);
    }
  }, [clientId, mapId, spreadsheetId, boundaryGroupId, constraints, blockSubset, filter, excludeDisconnectedBlocks, t]);

  const constraintsCountChangedRef = useRef<boolean>(false);
  const previousConstraintsCount = usePrevious(constraints.length);
  constraintsCountChangedRef.current = previousConstraintsCount !== constraints.length;

  useEffect(() => {
    if (!constraintsEnabled) {
      return;
    }

    const cancelToken = createCancelToken();
    const validateNow = constraintsCountChangedRef.current;

    const validateTask = executeDebounced(() => validateConstraints(cancelToken.token));
    if (validateNow) {
      validateTask.now();
    }

    return () => {
      cancelToken.cancel();
    };
  }, [constraintsEnabled, validateConstraints]);

  return {
    areConstraintsValid: !isLoading && (constraintsEnabled
      ? Array.from(validationData.values()).every(validation => validation.errorMessages.length === 0)
      : true),
    generalError,
    isLoading,
    validationData,
  };
};
